import React from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { KiteIcon } from '@kite/react-kite';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { processLink } from '../../shared/processLink';

export interface SmartLinkInputs {
    url: string;
    children: any;
    button?: boolean;
    classNames?: string[];
    newWindow?: boolean;
    ariaLabel?: string;
    download?: boolean;
    onClick?: (event?) => void;
    onMouseDown?: (event?) => void;
    onMouseEnter?: (event?) => void;
    onFocus?: (event?) => void;
    hide?: boolean;
    buttonVariant?: string;
    useIcon?: boolean;
    [`data-analytics-component`]?: string;
    [`data-analytics`]?: string;
    [`data-analytics-action`]?: string;
}

export const SmartLink = (inputs: SmartLinkInputs) => {
    const { isPolicy } = useLayoutContext();

    const target = inputs.newWindow ? `_blank` : undefined;
    const ariaLabel = inputs.ariaLabel ? inputs.ariaLabel : undefined;
    const finalClassNames = inputs.classNames ? [...inputs.classNames] : [];
    const tabIndex = inputs.hide ? -1 : undefined;

    if (inputs.button) {
        if (inputs.buttonVariant === `secondary`) {
            finalClassNames.push(`kite-button`);
            finalClassNames.push(`rk-button`);
            finalClassNames.push(`kite-button--secondary`);
            finalClassNames.push(`kite-button--shrinkwrap`);
        } else {
            finalClassNames.push(`kite-button`);
            finalClassNames.push(`kite-button--primary`);
            finalClassNames.push(`kite-button--lg`);
            finalClassNames.push(`kite-button--shrinkwrap`);
        }
    }

    const data = useStaticQuery(graphql`
        query {
            allSitePage {
                nodes {
                    pageContext
                }
            }
        }
    `);

    const classNames =
        finalClassNames.length > 0 ? finalClassNames.join(` `) : undefined;

    /*
        Decide if the link will be an anchor tag or Gatsby <Link>.
        <Link> will only be used for internal links that are not files.
    */
    const fixedUrl = processLink(inputs.url, isPolicy, data.allSitePage.nodes);

    let LinkElement = (
        <a
            className={classNames}
            href={fixedUrl}
            target={target}
            aria-label={ariaLabel}
            onClick={inputs.onClick}
            download={inputs.download}
            onMouseDown={inputs.onMouseDown}
            onMouseEnter={inputs.onMouseEnter}
            onFocus={inputs.onFocus}
            tabIndex={tabIndex}
            aria-hidden={inputs.hide}
            data-analytics-component={inputs[`data-analytics-component`]}
            data-analytics={inputs[`data-analytics`]}
            data-analytics-action={inputs[`data-analytics-action`]}
        >
            {inputs.children}
            {inputs.useIcon && (
                <KiteIcon icon="ki-linkout" id="smart-link-anchor-icon" />
            )}
        </a>
    );

    if (fixedUrl.startsWith(`/`) && !fixedUrl.includes(`.`)) {
        LinkElement = (
            <Link
                to={fixedUrl}
                className={classNames}
                title={ariaLabel}
                aria-label={ariaLabel}
                onClick={inputs.onClick}
                onMouseDown={inputs.onMouseDown}
                onMouseEnter={inputs.onMouseEnter}
                onFocus={inputs.onFocus}
                tabIndex={tabIndex}
                aria-hidden={inputs.hide}
                data-analytics-component={inputs[`data-analytics-component`]}
                data-analytics={inputs[`data-analytics`]}
                data-analytics-action={inputs[`data-analytics-action`]}
            >
                {inputs.children}
                {inputs.useIcon && (
                    <KiteIcon icon="ki-arrow-right" id="smart-link-link-icon" />
                )}
            </Link>
        );
    }

    return LinkElement;
};
